import AddIcon from '@mui/icons-material/Add';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Button from '@mui/material/Button';
import clsx from 'clsx';

import { ButtonLink } from '#components/router-links/button-link.js';

/**
 * How the sample door behaves:
 * - `open`: a demo already exists in the org; link straight into its results.
 * - `seed`: no demo yet; clicking provisions one on demand (lazy per-org seed).
 */
export type SampleDoor =
	| { mode: 'open'; demoProjectId: string; demoRevisionId: string }
	| { mode: 'seed'; onExplore: () => void; loading: boolean };

export interface EmptyStateChoicesProps {
	/**
	 * The sample-project door, either linking to an existing demo or seeding one, or
	 * `null` to hide it — e.g. when the user cannot manage the org and so cannot seed.
	 */
	sample: SampleDoor | null;
	/** Whether the create-your-own panel is offered; hidden when the user cannot manage projects. */
	canCreateProject: boolean;
	/**
	 * Called when the user picks the "create your own" path instead of the sample. What that
	 * means is the parent's call: opening the upload flow, or revealing a form in place.
	 */
	onCreateProject: () => void;
}

/**
 * The first-run paths: explore the sample project (recommended) when `sample` is set, and start the
 * create-your-own-project flow when `canCreateProject` is set. The sample door links into an existing
 * demo or seeds one on demand; what "create" does is left to the parent. With only one path present
 * the layout collapses to a single column.
 */
export function EmptyStateChoices({ sample, canCreateProject, onCreateProject }: EmptyStateChoicesProps) {
	const columnCount = (sample !== null ? 1 : 0) + (canCreateProject ? 1 : 0);
	return (
		<div className={clsx('grid w-full gap-3.5', columnCount === 2 ? 'grid-cols-2 max-sm:grid-cols-1' : 'grid-cols-1')}>
			{sample !== null && (
				<div className='flex flex-col gap-8 rounded-xl border border-gray-a6 bg-gray-2 p-8 transition-colors duration-150 hover:border-blue-a7'>
					<div className='flex flex-col items-center gap-3 text-center'>
						<div className='mb-1 flex items-center justify-center gap-3 font-mono text-xs tracking-wide text-gray-a10'>
							<span className='uppercase'>Sample design</span>
							<span className='text-blue-11'>Recommended</span>
						</div>
						<h2 className='text-3xl font-semibold'>Explore a sample project</h2>
						<p className='text-gray-a11'>See validation results on an example design — no upload required.</p>
					</div>
					{sample.mode === 'open' ?
						<ButtonLink
							from='/orgs/$orgName/projects/'
							to='./$projectId/revisions/$revisionId/results'
							params={{ projectId: sample.demoProjectId, revisionId: sample.demoRevisionId }}
							variant='contained'
							startIcon={<VisibilityOutlinedIcon />}
							className='mt-auto'
						>
							<span>Open sample project</span>
						</ButtonLink>
					:	<Button
							variant='contained'
							startIcon={<VisibilityOutlinedIcon />}
							loading={sample.loading}
							onClick={sample.onExplore}
							className='mt-auto'
						>
							<span>Open sample project</span>
						</Button>
					}
				</div>
			)}

			{canCreateProject && (
				<div className='flex flex-col gap-8 rounded-xl border border-gray-a6 bg-gray-2 p-8 transition-colors duration-150 hover:border-gray-7'>
					<div className='flex flex-col items-center gap-3 text-center'>
						<div className='mb-1 flex items-center justify-center font-mono text-xs tracking-wide text-gray-a10'>
							<span className='uppercase'>Your design</span>
						</div>
						<h2 className='text-3xl font-semibold'>Create your first project</h2>
						<p className='text-gray-a11'>Upload a revision and get findings back. Altium, OrCAD, Fusion 360, KiCad.</p>
					</div>
					<Button variant='outlined' startIcon={<AddIcon />} onClick={onCreateProject} className='mt-auto'>
						<span>Create project</span>
					</Button>
				</div>
			)}
		</div>
	);
}
